import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Linkedin, AlertCircle, Loader } from 'lucide-react';

const PHONE = '+353 85 862 5500';
const PHONE_HREF = 'tel:+353858625500';

const Contact = () => {
  const form = useRef();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const response = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
        setTimeout(() => setStatus('idle'), 6000);
      } else {
        throw new Error('Server error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 6000);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'contact@irongate.ie', href: 'mailto:contact@irongate.ie' },
    { icon: Phone, label: 'Phone', value: PHONE, href: PHONE_HREF },
    { icon: MapPin, label: 'Location', value: 'Dublin City, Ireland', href: null },
    { icon: Linkedin, label: 'LinkedIn', value: 'in/ionut-apostu', href: 'https://www.linkedin.com/in/ionut-apostu/' },
  ];

  const inputClass = `w-full px-4 py-3 rounded-lg text-white placeholder-gray-600 focus:outline-none transition-colors text-sm`;
  const inputStyle = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
  };
  const inputFocusStyle = { borderColor: 'rgba(0,212,255,0.5)' };

  return (
    <section id="contact" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0"
        style={{ background: 'radial-gradient(circle at 50% 50%, rgba(0,212,255,0.04), transparent 60%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center space-x-2 rounded-full px-4 py-1.5 mb-6"
            style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
            <Send className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs font-medium text-cyan-400 tracking-wide uppercase">Get In Touch</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
            Let's build<br />
            <span className="gradient-text">something great</span>
          </h2>
          <p className="text-gray-400 leading-relaxed">
            Whether you're planning a digital transformation, need AI expertise, or want to talk QA strategy — 
            we're happy to discuss how IronGate can help.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="lg:col-span-3 rounded-2xl p-8"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <form ref={form} onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2">Your Name *</label>
                  <input
                    type="text" name="name" required value={formData.name} onChange={handleChange}
                    className={inputClass} style={inputStyle} placeholder="John Smith"
                    onFocus={e => Object.assign(e.target.style, inputFocusStyle)}
                    onBlur={e => Object.assign(e.target.style, inputStyle)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2">Email Address *</label>
                  <input
                    type="email" name="email" required value={formData.email} onChange={handleChange}
                    className={inputClass} style={inputStyle} placeholder="john@company.com"
                    onFocus={e => Object.assign(e.target.style, inputFocusStyle)}
                    onBlur={e => Object.assign(e.target.style, inputStyle)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2">Company</label>
                <input
                  type="text" name="company" value={formData.company} onChange={handleChange}
                  className={inputClass} style={inputStyle} placeholder="Your Company"
                  onFocus={e => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={e => Object.assign(e.target.style, inputStyle)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2">Message *</label>
                <textarea
                  name="message" required rows={5} value={formData.message} onChange={handleChange}
                  className={`${inputClass} resize-none`} style={inputStyle}
                  placeholder="Tell us about your project or challenge..."
                  onFocus={e => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={e => Object.assign(e.target.style, inputStyle)}
                />
              </div>

              <button
                type="submit" disabled={status === 'sending'}
                className="w-full px-6 py-3.5 rounded-lg font-semibold text-white transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}
              >
                {status === 'sending' && <><Loader className="w-4 h-4 animate-spin" /><span>Sending...</span></>}
                {status === 'success' && <><CheckCircle className="w-4 h-4" /><span>Message Sent!</span></>}
                {status === 'error' && <><AlertCircle className="w-4 h-4" /><span>Failed — Try Again</span></>}
                {status === 'idle' && <><Send className="w-4 h-4" /><span>Send Message</span></>}
              </button>

              {status === 'success' && (
                <div className="p-4 rounded-lg text-sm text-emerald-400"
                  style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
                  ✓ Message sent! We'll get back to you soon.
                </div>
              )}
              {status === 'error' && (
                <div className="p-4 rounded-lg text-sm"
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>
                  ✗ Couldn't send — please try again or{' '}
                  <a href="mailto:contact@irongate.ie" className="underline hover:text-red-300">
                    email us directly
                  </a>.
                </div>
              )}
            </form>

            {/* Mailto fallback */}
            <p className="mt-5 text-xs text-gray-600 text-center">
              Prefer email?{' '}
              <a href="mailto:contact@irongate.ie" className="text-cyan-500 hover:text-cyan-400 underline transition-colors">
                contact@irongate.ie
              </a>
            </p>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-2 space-y-5">
            <div className="rounded-2xl p-7"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="text-lg font-bold text-white mb-6">Contact Details</h3>
              <div className="space-y-5">
                {contactInfo.map((info, i) => {
                  const Icon = info.icon;
                  return (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)' }}>
                        <Icon className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">{info.label}</div>
                        {info.href ? (
                          <a href={info.href}
                            target={info.icon === Linkedin ? '_blank' : undefined}
                            rel={info.icon === Linkedin ? 'noopener noreferrer' : undefined}
                            className="text-sm text-white hover:text-cyan-400 transition-colors font-medium">
                            {info.value}
                          </a>
                        ) : (
                          <div className="text-sm text-white font-medium">{info.value}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Why IronGate */}
            <div className="rounded-2xl p-7"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h3 className="text-base font-bold text-white mb-4">Why IronGate?</h3>
              <ul className="space-y-2.5">
                {[
                  '15 years enterprise experience',
                  'Fintech, Payments & Healthcare expertise',
                  'Hands-on — we write code, not just decks',
                  'Business-focused outcomes',
                  'Agile & transparent approach',
                ].map((item, i) => (
                  <li key={i} className="flex items-center space-x-2 text-sm text-gray-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Availability */}
            <div className="rounded-2xl p-5"
              style={{
                background: 'linear-gradient(135deg, rgba(0,212,255,0.07) 0%, rgba(0,102,255,0.07) 100%)',
                border: '1px solid rgba(0,212,255,0.2)',
              }}>
              <div className="flex items-center space-x-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-sm font-semibold text-white">Available for Projects</span>
              </div>
              <p className="text-xs text-gray-400">Currently accepting new engagements</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
